import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Modal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasShown, setHasShown] = useState(false);
  const { FiX, FiLock, FiCheckCircle } = FiIcons;

  // Listen for form submission messages
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin.includes('yinguru.com')) {
        if (
          event.data === 'formSubmitted' || 
          event.data?.type === 'formSubmitted' || 
          event.data?.event_type === 'formSubmitted'
        ) {
          window.location.href = 'https://learn.yinguru.com/';
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasShown) {
        setIsOpen(true);
        setHasShown(true);
      }
    }, 20000);
    return () => clearTimeout(timer);
  }, [hasShown]);

  useEffect(() => {
    const handleOpenModal = () => {
      setIsOpen(true);
      setHasShown(true);
    };
    window.addEventListener('openModal', handleOpenModal);
    window.openModal = handleOpenModal;

    return () => {
      window.removeEventListener('openModal', handleOpenModal);
      delete window.openModal;
    };
  }, []);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  return (
    <>
      {/* 
          PRE-RENDERED IFRAME (Hidden) 
          This loads the form in the background as soon as the page loads.
      */}
      <div className="fixed pointer-events-none opacity-0 left-[-9999px] top-[-9999px]">
        <iframe
          src="https://dmc.yinguru.com/widget/form/grhy1HBbhwzS4DPzdzb4"
          id="pre-loader-form"
          title="Pre-loader"
        />
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/95 backdrop-blur-md"
              onClick={() => setIsOpen(false)}
            />

            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative w-full max-w-xl animated-border-box"
            >
              <div className="animated-border-content bg-pbi-grey overflow-hidden shadow-2xl">
                <div className="bg-pbi-dark border-b border-gray-800 p-4 flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-pbi-yellow/10 rounded-full flex items-center justify-center">
                      <SafeIcon icon={FiLock} className="text-pbi-yellow text-xl" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold text-lg leading-tight">Join Academy</h3>
                      <p className="text-gray-400 text-xs flex items-center gap-1">
                        <SafeIcon icon={FiCheckCircle} className="text-green-500" />
                        Free 90-Day Roadmap
                      </p>
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    className="text-gray-400 hover:text-white transition-colors p-2"
                    onClick={() => setIsOpen(false)}
                  >
                    <SafeIcon icon={FiX} className="text-2xl" />
                  </motion.button>
                </div>

                <div className="p-0 bg-white relative overflow-hidden h-[600px]">
                  {/* The actual iframe used in display */}
                  <iframe
                    src="https://dmc.yinguru.com/widget/form/grhy1HBbhwzS4DPzdzb4"
                    style={{ width: '100%', height: '100%', border: 'none' }}
                    id="inline-grhy1HBbhwzS4DPzdzb4"
                    title="Power BI Career Roadmap Form"
                    // No loading="lazy" here to ensure it hits the cache from the pre-loader
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Modal;